import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GraduationCap, CheckCircle, XCircle, Loader } from "lucide-react";
import { API_BASE_URL } from "../../config/apiConfig";
import bg from "../../assets/first_bg.png";

/**
 * JoinTeacherPage
 *
 * Handles the shareable teacher invite link:
 *   /join-teacher?code=TC-{teacherUserId}
 *
 * Behaviour:
 *  - Logged in as student  → calls POST /api/teachers/join/:code → success screen
 *  - Not logged in         → saves code to localStorage, redirects to /login
 *  - Logged in as non-student → shows an error (only students can join)
 */
const JoinTeacherPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [status, setStatus] = useState("loading"); // loading | success | error | needs_login
    const [message, setMessage] = useState("");
    const [teacherName, setTeacherName] = useState("");

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const code = params.get("code") || "";

        if (!code) {
            setStatus("error");
            setMessage("No teacher code found in the link. Please ask your teacher to share the correct link.");
            return;
        }

        const token = localStorage.getItem("token");

        if (!token) {
            // Save code so we can auto-join after login (login page can check this)
            localStorage.setItem("pending_teacher_code", code);
            setStatus("needs_login");
            return;
        }

        // Attempt to join
        const joinTeacher = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/teachers/join/${code}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                const data = await res.json();

                if (res.ok) {
                    setTeacherName(data.teacherName || "your teacher");
                    setStatus("success");
                    // Clear any stale pending code
                    localStorage.removeItem("pending_teacher_code");
                    // Redirect to dashboard after a short delay
                    setTimeout(() => navigate("/dashboard"), 3000);
                } else {
                    setStatus("error");
                    setMessage(data.message || "Failed to join teacher's class. Please try again.");
                }
            } catch {
                setStatus("error");
                setMessage("A network error occurred. Check your connection and try again.");
            }
        };

        joinTeacher();
    }, [location.search, navigate]);

    return (
        <div
            className="flex justify-center items-center min-h-screen py-4"
            style={{
                backgroundImage: `url(${bg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundColor: "#2148C0",
            }}
        >
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 md:p-12 max-w-md w-[90%] text-center">
                {/* Logo */}
                <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                        <GraduationCap className="w-9 h-9 text-white" />
                    </div>
                </div>

                <h1 className="text-2xl font-bold text-white mb-2">TeeChaa</h1>
                <p className="text-white/70 text-sm mb-8">Teacher Invite</p>

                {/* ── Loading ── */}
                {status === "loading" && (
                    <div>
                        <Loader className="w-10 h-10 text-white mx-auto mb-4 animate-spin" />
                        <p className="text-white font-medium">Connecting you to your teacher's class…</p>
                    </div>
                )}

                {/* ── Success ── */}
                {status === "success" && (
                    <div>
                        <CheckCircle className="w-14 h-14 text-green-300 mx-auto mb-4" />
                        <h2 className="text-xl font-bold text-white mb-2">You're in!</h2>
                        <p className="text-white/80 mb-6">
                            You've been successfully connected to <strong className="text-white">{teacherName}</strong>'s class.
                        </p>
                        <p className="text-white/60 text-sm">Taking you to your dashboard…</p>
                    </div>
                )}

                {/* ── Needs Login ── */}
                {status === "needs_login" && (
                    <div>
                        <div className="w-14 h-14 bg-yellow-300/20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <GraduationCap className="w-8 h-8 text-yellow-200" />
                        </div>
                        <h2 className="text-xl font-bold text-white mb-3">Log in to join</h2>
                        <p className="text-white/70 text-sm mb-6 leading-relaxed">
                            You need a TeeChaa account to join your teacher's class. Log in or create an account, then return to this link to complete the connection.
                        </p>
                        <div className="flex flex-col gap-3">
                            <button
                                onClick={() => navigate("/login")}
                                className="w-full py-3 bg-white text-[#2148C0] font-bold rounded-xl hover:bg-gray-100 transition-colors"
                            >
                                Log In
                            </button>
                            <button
                                onClick={() => {
                                    // Pass code in URL too, so signup page can re-save it
                                    // if localStorage was cleared between page loads
                                    const params = new URLSearchParams(location.search);
                                    const code = params.get("code") || "";
                                    navigate(`/signup?teacher_code=${encodeURIComponent(code)}`);
                                }}
                                className="w-full py-3 bg-white/20 hover:bg-white/30 text-white font-medium rounded-xl transition-colors"
                            >
                                Create Account
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Error ── */}
                {status === "error" && (
                    <div>
                        <XCircle className="w-14 h-14 text-red-300 mx-auto mb-4" />
                        <h2 className="text-xl font-bold text-white mb-3">Something went wrong</h2>
                        <p className="text-white/70 text-sm mb-6 leading-relaxed">{message}</p>
                        <button
                            onClick={() => navigate("/dashboard")}
                            className="w-full py-3 bg-white text-[#2148C0] font-bold rounded-xl hover:bg-gray-100 transition-colors"
                        >
                            Go to Dashboard
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default JoinTeacherPage;
